import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const ScheduleTable = ({ schedules, routes, onEdit, onDelete, onBulkDelete, selectedSchedules, onSelectSchedule, onSelectAll }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'start_time', direction: 'asc' });

  const getRouteName = (routeId) => {
    const route = routes?.find(r => r?.id === routeId);
    return route ? route?.route_name : 'Bilinmeyen Rota';
  };

  const formatTime = (time) => {
    return time || '--:--';
  };

  const formatDays = (days) => {
    if (!days || days?.length === 0) return 'Gün seçilmemiş';
    
    const dayMap = {
      monday: 'Pzt',
      tuesday: 'Sal',
      wednesday: 'Çar',
      thursday: 'Per',
      friday: 'Cum',
      saturday: 'Cmt',
      sunday: 'Paz'
    };

    return days?.map(day => dayMap?.[day] || day)?.join(', ');
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedSchedules = [...schedules]?.sort((a, b) => {
    const aValue = a?.[sortConfig?.key];
    const bValue = b?.[sortConfig?.key];
    
    if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ columnKey }) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ChevronsUpDown" size={16} color="var(--color-muted-foreground)" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ChevronUp" size={16} color="var(--color-primary)" />
      : <Icon name="ChevronDown" size={16} color="var(--color-primary)" />;
  };

  return (
    <div className="space-y-4">
      {selectedSchedules?.length > 0 && (
        <div className="flex items-center justify-between p-4 bg-primary/10 rounded-lg">
          <p className="text-sm font-medium text-foreground">
            {selectedSchedules?.length} program seçildi
          </p>
          <Button
            variant="destructive"
            size="sm"
            onClick={onBulkDelete}
            iconName="Trash2"
            iconPosition="left"
          >
            Seçilenleri Sil
          </Button>
        </div>
      )}
      <div className="hidden lg:block overflow-x-auto rounded-lg border border-border">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="p-4 text-left">
                <Checkbox
                  checked={schedules?.length > 0 && selectedSchedules?.length === schedules?.length}
                  onChange={(e) => onSelectAll(e?.target?.checked)}
                />
              </th>
              <th className="p-4 text-left">
                <button
                  onClick={() => handleSort('route_id')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-smooth"
                >
                  Rota
                  <SortIcon columnKey="route_id" />
                </button>
              </th>
              <th className="p-4 text-left">
                <button
                  onClick={() => handleSort('start_time')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-smooth"
                >
                  Başlangıç
                  <SortIcon columnKey="start_time" />
                </button>
              </th>
              <th className="p-4 text-left">
                <button
                  onClick={() => handleSort('end_time')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-smooth"
                >
                  Bitiş
                  <SortIcon columnKey="end_time" />
                </button>
              </th>
              <th className="p-4 text-left">
                <button
                  onClick={() => handleSort('duration_minutes')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-smooth"
                >
                  Süre
                  <SortIcon columnKey="duration_minutes" />
                </button>
              </th>
              <th className="p-4 text-left">
                <button
                  onClick={() => handleSort('frequency_minutes')}
                  className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-smooth"
                >
                  Frekans
                  <SortIcon columnKey="frequency_minutes" />
                </button>
              </th>
              <th className="p-4 text-left text-sm font-semibold text-foreground">
                Günler
              </th>
              <th className="p-4 text-right text-sm font-semibold text-foreground">
                İşlemler
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedSchedules?.map((schedule) => (
              <tr key={schedule?.id} className="hover:bg-muted/50 transition-smooth">
                <td className="p-4">
                  <Checkbox
                    checked={selectedSchedules?.includes(schedule?.id)}
                    onChange={() => onSelectSchedule(schedule?.id)}
                  />
                </td>
                <td className="p-4">
                  <p className="text-sm font-medium text-foreground">
                    {getRouteName(schedule?.route_id)}
                  </p>
                </td>
                <td className="p-4">
                  <p className="text-sm text-foreground data-text">
                    {formatTime(schedule?.start_time)}
                  </p>
                </td>
                <td className="p-4">
                  <p className="text-sm text-foreground data-text">
                    {formatTime(schedule?.end_time)}
                  </p>
                </td>
                <td className="p-4">
                  <p className="text-sm text-foreground data-text">
                    {schedule?.duration_minutes} dk
                  </p>
                </td>
                <td className="p-4">
                  <p className="text-sm text-foreground data-text">
                    {schedule?.frequency_minutes} dk
                  </p>
                </td>
                <td className="p-4">
                  <p className="text-xs text-muted-foreground">
                    {formatDays(schedule?.days_mask)}
                  </p>
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onEdit(schedule)}
                      className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-smooth active-press"
                      aria-label="Düzenle"
                    >
                      <Icon name="Pencil" size={16} color="var(--color-primary)" />
                    </button>
                    <button
                      onClick={() => onDelete(schedule?.id)}
                      className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-destructive/10 transition-smooth active-press"
                      aria-label="Sil"
                    >
                      <Icon name="Trash2" size={16} color="var(--color-destructive)" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="lg:hidden space-y-3">
        {sortedSchedules?.map((schedule) => (
          <div key={schedule?.id} className="bg-card border border-border rounded-lg p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <Checkbox
                  checked={selectedSchedules?.includes(schedule?.id)}
                  onChange={() => onSelectSchedule(schedule?.id)}
                  className="mt-1"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-foreground truncate">
                    {getRouteName(schedule?.route_id)}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDays(schedule?.days_mask)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onEdit(schedule)}
                  className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-muted transition-smooth active-press"
                  aria-label="Düzenle"
                >
                  <Icon name="Pencil" size={18} color="var(--color-primary)" />
                </button>
                <button
                  onClick={() => onDelete(schedule?.id)}
                  className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-destructive/10 transition-smooth active-press"
                  aria-label="Sil"
                >
                  <Icon name="Trash2" size={18} color="var(--color-destructive)" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Başlangıç</p>
                <p className="text-sm font-medium text-foreground data-text">
                  {formatTime(schedule?.start_time)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Bitiş</p>
                <p className="text-sm font-medium text-foreground data-text">
                  {formatTime(schedule?.end_time)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Süre</p>
                <p className="text-sm font-medium text-foreground data-text">
                  {schedule?.duration_minutes} dakika
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Frekans</p>
                <p className="text-sm font-medium text-foreground data-text">
                  {schedule?.frequency_minutes} dakika
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
      {schedules?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 px-4">
          <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
            <Icon name="Clock" size={32} color="var(--color-muted-foreground)" />
          </div>
          <p className="text-base font-medium text-foreground mb-2">Program Bulunamadı</p>
          <p className="text-sm text-muted-foreground text-center">
            Henüz hiç program eklenmemiş. Yeni program eklemek için yukarıdaki butonu kullanın.
          </p>
        </div>
      )}
    </div>
  );
};

export default ScheduleTable;